import * as Sentry from '@sentry/react';
import axios from 'axios';
import classNames from 'classnames';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import React from 'react';

import Headlines from '../../components/Headlines';
import ConfigRentContext from '../../service/context/config-rent';
import useAppStoreRent from '../../service/store/appRent';
import useOrderStoreRent from '../../service/store/orderRent';
import type { AppConfigRental, IDeliveryOption, IPickupOption, OrderApiResponse } from '../../types';
import { prepareAddress } from '../../utils/validate';

import StepConfirmation from './../vos/steps/StepConfirmation';
import { headlineContentRent, MAX_STEPS_RENT, STEP_REVIEW_RENT } from './constants';
import RentCart from './RentCart';
import StepProductSelect from './steps/ProductSelect';
import StepCustomerData from './steps/StepCustomerData';
import StepReview from './steps/StepReview';

const RentForm: FC = () => {
  const [ loading, setLoading ] = useState<boolean>(false);
  const [ config, setConfig ] = useState<AppConfigRental | null>(null);
  const [ orderLoading, setOrderLoading ] = useState<boolean>(false);
  const [ orderSuccess, setOrderSuccess ] = useState<boolean>(false);
  const [ orderError, setOrderError ] = useState<boolean>(false);
  const [ defaultDeliveryOption, setDefaultDeliveryOption ] = useState<IDeliveryOption | undefined>();
  const [ defaultPickupOption, setDefaultPickupOption ] = useState<IPickupOption | undefined>();
  const setStep = useAppStoreRent(state => state.setStep);
  const setProducts = useAppStoreRent(state => state.setProducts);
  const setFilteredProducts = useAppStoreRent(state => state.setFilteredProducts);
  const setAvailableFilter = useAppStoreRent(state => state.setAvailableFilter);
  const setOrderValue = useOrderStoreRent(state => state.setValue);

  const { order, resetOrderState } = useOrderStoreRent(state => state) || {};
  const { step, resetAppState } = useAppStoreRent(state => state);

  const resetApp = (): void => {
    // eslint-disable-next-line no-console
    console.log({ step, orderSuccess, orderLoading });

    if (
      step === MAX_STEPS_RENT
      && orderSuccess
      && !orderLoading
    ) {
      resetOrderState({
        ... defaultDeliveryOption && { delivery_option: defaultDeliveryOption },
        ... defaultPickupOption && { pickup_option: defaultPickupOption }
      });
      resetAppState();
      setOrderSuccess(false);
      setProducts(config?.products || []);
      setFilteredProducts(config?.products || []);
      setAvailableFilter({
        system: config?.system || [],
        type: config?.type || [],
        kontur: config?.kontur || [],
        material: config?.material || [],
        manufacturer: config?.manufacturers || []
      });

      // eslint-disable-next-line no-console
      console.log('reset');
    }
  };

  const placeOrderHelper = async (endpoint = 'order-rent'): Promise<void> => {
    if (!orderLoading) {
      try {
        setOrderLoading(true);
        setStep(MAX_STEPS_RENT);
        const { data } = await axios.post<OrderApiResponse>(`${process.env.REACT_APP_API_URL}/${endpoint}/`, {
          nonce: config?.nonce,
          order: {
            ...order,
            address: prepareAddress(order?.address),
            deliveryAddress: prepareAddress(order?.deliveryAddress),
            invoiceAddress: prepareAddress(order?.invoiceAddress),
            invoiceRecipient: prepareAddress(order?.invoiceRecipient)
          }
        });

        if (data) {
          setOrderLoading(false);
          if (data.ok) {
            setOrderSuccess(true);
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        setOrderLoading(false);
        setOrderError(true);
      }
    }
  };

  const placeOrder = async (): Promise<void> => {
    void await placeOrderHelper();
  };

  const goToReview = async (): Promise<void> => {
    setStep(STEP_REVIEW_RENT);
  };

  useEffect(() => {
    const getData = async (): Promise<void> => {
      setLoading(true);
      const { data } = await axios.get<AppConfigRental>(
        `${process.env.REACT_APP_API_URL}/config-rent/`
      );

      setConfig(data);

      if (data.delivery_options && data.delivery_options.length) {
        setOrderValue(data.delivery_options[0], 'delivery_option');

        setDefaultDeliveryOption(data.delivery_options[0]);
      }

      if (data.pickup_options && data.pickup_options.length) {
        setOrderValue(data.pickup_options[0], 'pickup_option');

        setDefaultPickupOption(data.pickup_options[0]);
      }

      setLoading(false);
      setProducts(data.products);
      setFilteredProducts(data.products);
      setAvailableFilter({
        system: data.system,
        type: data.type,
        kontur: data.kontur,
        material: data.material,
        manufacturer: data.manufacturers
      });
    };

    void getData();
  }, [ setAvailableFilter, setFilteredProducts, setOrderValue, setProducts ]);

  if (loading) {
    return (
      <div>
        Loading...
      </div>
    );
  }

  return (
    <ConfigRentContext.Provider value={config}>
      <div className="app">
        <div className="container">
          <Headlines
            reset={resetApp}
            setStep={setStep}
            step={step}
            headlines={headlineContentRent}
            maxSteps={MAX_STEPS_RENT}
          />
        </div>
        <div className={classNames('container', {
          'has-content-navi': step > 1 && step < 3
        })}>
          <div className="left">
            {step === 1 && (
              <StepProductSelect />
            )}
            {step === 2 && (
              <StepCustomerData placeOrder={goToReview} />
            )}
            {step === STEP_REVIEW_RENT && (
              <StepReview onSubmit={placeOrder} />
            )}
            {step === MAX_STEPS_RENT && (
              <StepConfirmation
                reset={resetApp}
                resetButtonText="Weiteren Mietauftrag erstellen"
                error={orderError}
              />
            )}
          </div>
          {step !== STEP_REVIEW_RENT && (
            <div className="right">
              <RentCart placeOrder={goToReview} />
              <div id="cart-portal" className="cart-portal-rent"></div>
            </div>
          )}
        </div>
      </div>
    </ConfigRentContext.Provider>
  );
};

export default RentForm;
